import React from 'react';
import { TDR } from 'tdr-common';
import { ModalWithTrigger } from '../modal/modal';
import { PricingBreakdown } from '../modal/info/pricing-breakdown';
import { SearchFilters } from '../../../routes/AvailabilityGuard';
import { analytics, INFO_CLICKED_EVENT } from '../../../helpers/analytics';

export enum PriceDisplayVariant {
	CAROUSEL = 'carousel',
	TABLE_DRAWER = 'table-drawer'
}

type PriceDisplayProps = {
	restaurant: TDR.Restaurant;
	table: TDR.Table;
	searchFilters: SearchFilters;
	estimatedInvoice?: Partial<TDR.Invoice>;
	timeslot?: TDR.TimeSlot;
	featureFlags?: { inclusivePricing?: boolean; hideFreePricing?: boolean };
	variant?: PriceDisplayVariant;
	className?: string;
};

export const PriceDisplay = ({
	restaurant,
	searchFilters,
	estimatedInvoice,
	timeslot,
	featureFlags,
	variant = PriceDisplayVariant.CAROUSEL,
	className = ''
}: PriceDisplayProps) => {
	// Calculate the appropriate price to display
	const getDisplayPrice = (): number => {
		const fallbackPrice = timeslot?.isPerGuest
			? timeslot?.regularPrice * searchFilters?.groupSize
			: timeslot?.regularPrice;

		// Inclusive pricing: show total (includes taxes)
		if (featureFlags?.inclusivePricing) {
			return estimatedInvoice?.total || fallbackPrice;
		}

		// Per-guest pricing: show unit price per guest
		if (timeslot?.isPerGuest && estimatedInvoice?.lineItems?.[0]?.charges?.[0]?.amount) {
			return estimatedInvoice.lineItems[0].charges[0].amount;
		}

		// Non-per-guest pricing: show subtotal
		return estimatedInvoice?.subtotal || fallbackPrice;
	};

	const displayPrice = getDisplayPrice();
	const currencyConfig = TDR.CurrencyConfig[estimatedInvoice?.currency || TDR.Currency.USD];

	// Only show "per guest" suffix when:
	// 1. The timeslot is per-guest pricing AND
	// 2. We're NOT in inclusive pricing mode (because inclusive pricing shows totals)
	const shouldShowPerGuestSuffix = timeslot?.isPerGuest && !featureFlags?.inclusivePricing;

	// Don't render if price is undefined or 0
	if (displayPrice === undefined) {
		return null;
	}
	else if (displayPrice === 0 && featureFlags?.hideFreePricing) {
		return null;
	}

	const placement = currencyConfig.placement;
	const symbol = currencyConfig.symbol;

	// Variant-specific styling
	const getVariantStyles = () => {
		switch (variant) {
			case PriceDisplayVariant.TABLE_DRAWER:
				return {
					container: 'font-sans cursor-pointer transition-transform duration-200 inline-block hover:scale-110 whitespace-nowrap',
					price: 'font-medium text-2xl',
					suffix: 'text-sm text-tablz-light-gray-3 ml-1'
				};
			case PriceDisplayVariant.CAROUSEL:
			default:
				return {
					container: 'font-sans cursor-pointer transition-transform duration-200 inline-block hover:scale-110',
					price: 'font-medium',
					suffix: 'text-sm text-tablz-light-gray-3 ml-1'
				};
		}
	};

	const styles = getVariantStyles();

	const priceElement = (
		<button
			type="button"
			className={`${styles.container} ${className}`}
			onClick={(event) => {
				// Only stop propagation to prevent card navigation
				// Don't preventDefault so Radix can handle the modal opening
				event.stopPropagation();
				if (variant === PriceDisplayVariant.CAROUSEL) {
					analytics.track(INFO_CLICKED_EVENT, {
						purpose: 'Pricing Breakdown',
						location: 'Inventory Carousel Card'
					});
				}
				else if (variant === PriceDisplayVariant.TABLE_DRAWER) {
					analytics.track(INFO_CLICKED_EVENT, {
						purpose: 'Pricing Breakdown',
						location: 'Table Drawer'
					});
				}
			}}
		>
			<span className="underline underline-offset-[4px] decoration-1">
				{placement === 'before' ? (
					<sup className="font-features sups text-xs text-tablz-light-gray-3">{symbol}</sup>
				) : null}
				<span className={styles.price}>
					{Number.isInteger(displayPrice) ? displayPrice : displayPrice ? displayPrice.toFixed(2) : '0'}
				</span>
				{placement === 'after' ? (
					<sup className="font-features sups text-xs text-tablz-light-gray-3">{symbol}</sup>
				) : null}
				{shouldShowPerGuestSuffix && (
					<span className={styles.suffix}>pp</span>
				)}
			</span>
		</button>
	);

	return (
		<ModalWithTrigger trigger={priceElement}>
			<PricingBreakdown
				estimatedInvoice={estimatedInvoice}
				timeSlot={timeslot}
				guestCount={searchFilters.groupSize}
				restaurant={restaurant}
			/>
		</ModalWithTrigger>
	);
};
